import { motion, AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { X, Calendar } from "lucide-react";

export default function ModalEvent({ event, setSelectedEvent }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={() => setSelectedEvent(null)}
    >
      {/* Overlay avec dégradé  */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-vertLogo/20 to-black/60" />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative bg-white max-w-4xl w-full rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header avec gradient */}
        <div className="relative bg-gradient-to-r from-vertLogo to-vertLogo/80 text-white px-8 py-10">
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setSelectedEvent(null)}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-block px-4 py-1 rounded-full bg-white/20 backdrop-blur-sm text-sm font-semibold mb-4"
          >
            Événement à venir
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold leading-tight"
          >
            {event.title}
          </motion.h2>

          {event.date && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-center gap-2 mt-2 text-sm font-medium"
            >
              <Calendar className="w-5 h-5" />
              <span>{event.date}</span>
            </motion.div>
          )}
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="prose prose-lg max-w-none"
          >
            <ReactMarkdown
              skipHtml={false}
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    className="text-3xl text-vertLogo font-bold mt-8 mb-4 first:mt-0"
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className="text-2xl text-vertLogo font-bold mt-6 mb-3"
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className="text-xl text-vertLogo font-semibold mt-4 mb-2"
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <p
                    className="mb-4 leading-relaxed text-gray-700"
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="space-y-2 my-4" {...props} />
                ),
                li: ({ node, ...props }) => (
                  <li className="ml-4 text-gray-700 flex items-start">
                    <span className="text-vertLogo mr-2 mt-1.5">•</span>
                    <span {...props} />
                  </li>
                ),
                strong: ({ node, ...props }) => (
                  <strong className="text-vertLogo font-semibold" {...props} />
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    className="border-l-4 border-vertLogo pl-4 italic text-gray-600 my-4"
                    {...props}
                  />
                ),
              }}
            >
              {event.longDesc}
            </ReactMarkdown>
          </motion.div>
        </div>

        {/* Footer avec CTA */}
        {event.isActive && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="px-8 py-6 bg-gradient-to-t from-gray-50 to-transparent border-t border-gray-100"
          >
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={event.link}
                className="group relative px-8 py-4 bg-gradient-to-r from-vertLogo to-vertLogo/90 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {event.contenuLink}
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    →
                  </motion.span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </motion.a>

              <button
                onClick={() => setSelectedEvent(null)}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Fermer
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
